import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, cn, Popover } from '@c2fo/liquidity';
import { BellExclamationDuotoneIcon, InfoCircleIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { BuyerGroup } from '@/enterprise/data/useGroupByBuyer';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import useExchangeRates from '@/shared/data/useExchangeRates';
import getActiveOfferCount from '@/utils/getActiveOfferCount';
import getBuyerStats from '@/utils/getBuyerStats';
import useIsNewDivision from '@/utils/useIsNewDivision';
import useLocaleFormat from '@/utils/useLocaleFormat';
import AcceptanceTooltip from './AcceptanceTooltip';

const OfferDetails = ({ buyer }: { buyer: BuyerGroup }) => {
  const { t } = useTranslation();
  const { isNewDivision } = useIsNewDivision();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const { listenToMarketStats } = useMarketStatsStatus();
  const { asCurrency, asNumber, asPercent } = useLocaleFormat();
  const listeningForMarketStat = listenToMarketStats(
    buyer.takerMarkets.map(({ marketUuid, takerDivisionId, takerDivisionUuid, makerOrganizationUuid }) => ({
      marketUuid,
      takerId: takerDivisionId,
      takerDivisionUuid,
      makerOrganizationUuid,
    }))
  );
  const stats = useMemo(
    () =>
      getBuyerStats({
        buyer,
        exchangeRates,
        isNewDivision,
        selectedCurrency,
        includeAwardedInvoices: true,
      }),
    [buyer, exchangeRates, isNewDivision, selectedCurrency]
  );
  const { activeOfferCount, activeOfferCountDisplay } = useMemo(
    () => getActiveOfferCount(buyer.takerMarkets),
    [buyer.takerMarkets]
  );

  const offerDetailStats = useMemo(
    () =>
      buyer.takerMarkets.reduce(
        (acc, takerMarket) => {
          if (takerMarket.offerConfig.isEnabled) {
            acc.hasEligibleInvoices =
              acc.hasEligibleInvoices || takerMarket.eligibleInvoiceCount + takerMarket.awardedInvoiceCount > 0;
            acc.allHaveZeroTotalInvoices = acc.allHaveZeroTotalInvoices && takerMarket.totalInvoiceCount === 0;
            acc.allHaveZeroEligibleInvoices = acc.allHaveZeroEligibleInvoices && takerMarket.eligibleInvoiceCount === 0;
          }

          return acc;
        },
        {
          hasEligibleInvoices: false,
          allHaveZeroTotalInvoices: true,
          allHaveZeroEligibleInvoices: true,
        }
      ),
    [buyer.takerMarkets]
  );

  // only consider active offers for acceptance display
  const showAcceptancePercentage = activeOfferCount > 0 && offerDetailStats.hasEligibleInvoices;
  const noAvailableInvoices = activeOfferCount > 0 && offerDetailStats.allHaveZeroTotalInvoices;
  const noIncludedInvoices = activeOfferCount > 0 && offerDetailStats.allHaveZeroEligibleInvoices;

  return (
    <div className="flex w-full flex-col items-center justify-center gap-x-8 gap-y-2 rounded-lg bg-canvas px-4 py-2 @[768px]/activity-card:w-52 xl:w-[544px] xl:flex-row">
      <div className="xl:flex xl:flex-1 xl:justify-center">
        <Chip
          className="bg-indigo-100 text-indigo-900"
          label={`${t('core.activeOffers', {
            count: activeOfferCountDisplay,
            formattedCount: asNumber({ value: activeOfferCountDisplay }),
          })}`}
        />
      </div>
      <div className="text-center xl:flex xl:flex-1 xl:flex-col xl:justify-center">
        <div className="text-xs text-text-secondary">{t('core.estimatedEarlyPaymentAmount')}</div>
        <div className={cn('font-medium', { 'blur-sm': listeningForMarketStat })}>
          {asCurrency(stats.estimatedEarlyPaymentAmount, selectedCurrency)}
        </div>
      </div>
      {showAcceptancePercentage ? (
        <div
          className={cn(
            'flex shrink-0 flex-nowrap gap-1 text-nowrap font-medium text-red-700 xl:flex-1 xl:items-center xl:justify-center',
            {
              'text-green-700': stats.acceptancePercentage >= 0.9,
              'blur-sm': listeningForMarketStat,
            }
          )}
        >
          {stats.acceptancePercentage < 0.9 ? (
            <AcceptanceTooltip>
              <span className="flex items-center gap-1">
                <BellExclamationDuotoneIcon className="size-5 fill-red-700" />
                {asPercent({ value: stats.acceptancePercentage })} {t('core.estimatedAcceptance')}
              </span>
            </AcceptanceTooltip>
          ) : (
            <>
              {asPercent({ value: stats.acceptancePercentage })} {t('core.estimatedAcceptance')}
            </>
          )}
        </div>
      ) : noAvailableInvoices ? (
        <div className="flex gap-1 text-center font-medium xl:flex xl:flex-1 xl:items-center xl:justify-center">
          {t('core.noInvoices')}
          <Popover>
            <Popover.Trigger>
              <InfoCircleIcon className="size-4 fill-text-secondary" />
            </Popover.Trigger>
            <Popover.Content arrow side="bottom" collisionPadding={24}>
              <div className="w-60 space-y-4 text-center text-sm">
                <div>{t('core.noInvoicesTooltip')}</div>
                <div>{t('core.noInvoicesTooltip2')}</div>
              </div>
            </Popover.Content>
          </Popover>
        </div>
      ) : noIncludedInvoices ? (
        <div className="flex gap-1 text-center font-medium xl:flex xl:flex-1 xl:items-center xl:justify-center">
          {t('core.noInvoicesIncluded')}
          <Popover>
            <Popover.Trigger>
              <InfoCircleIcon className="size-4 fill-text-secondary" />
            </Popover.Trigger>
            <Popover.Content arrow side="bottom" collisionPadding={24}>
              <div className="w-60 space-y-4 text-center text-sm">
                <div>{t('core.noInvoicesIncludedTooltip')}</div>
                <div>{t('homepage.tooltipContent3')}</div>
              </div>
            </Popover.Content>
          </Popover>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-1 text-center font-medium xl:flex xl:flex-1 xl:items-center xl:justify-center">
          --
        </div>
      )}
    </div>
  );
};

export default OfferDetails;
